import React, { useState } from "react"
import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import { graphql, useStaticQuery } from "gatsby"

import ResolvedImage from "@app/components/ResolvedImage"
import ResolvedContent from "@app/components/ResolvedContent"
import { ProductIngredient } from "@app/hooks/useProductIngredients"

type Props = {
  ingredients: ProductIngredient[]
  title: string
  subTitle: string
}

const ProductTabIngredients: React.FC<Props> = ({ ingredients, title, subTitle }) => {
  const { staticIngredient } = useStaticQuery<GatsbyTypes.StaticProductIngredientsQuery>(graphql`
    query StaticProductIngredients {
      staticIngredient: sanityTemplateProduct {
        ingredientsDisclaimer
      }
    }
  `)

  const { isOpen, onClose, onOpen } = useDisclosure()

  const [activeIngredient, setActiveIngredient] = useState(null)

  const handleClickedIngredient = (ingredient: any) => {
    setActiveIngredient(ingredient)
    onOpen()
  }

  return ingredients?.length > 0 ? (
    <Box p={{ base: "16px", md: "32px" }}>
      <Flex flexDir="column" gap="16px">
        <Heading fontWeight={700}>{title}</Heading>
        <Box>
          <Text mb="10px">{subTitle}</Text>
          {ingredients?.map((ingredient: any, index: number) => (
            <Button
              key={ingredient.handle.current}
              variant="fillWhite"
              fontSize="16px"
              lineHeight={1.5}
              type="button"
              _hover={{ background: "transparent", textDecoration: "underline" }}
              title={ingredient?.title || ""}
              aria-label={ingredient?.title || ""}
              cursor="pointer"
              p={0}
              minW="unset"
              onClick={() => handleClickedIngredient(ingredient)}
            >
              {ingredient?.title}

              {index !== ingredients?.length - 1 && <span>,&nbsp;</span>}
            </Button>
          ))}
        </Box>
        {staticIngredient?.ingredientsDisclaimer && (
          <Text fontSize="12px" lineHeight="20px">
            {staticIngredient?.ingredientsDisclaimer}
          </Text>
        )}
      </Flex>
      <ProductIngredientModal ingredient={activeIngredient} isOpen={isOpen} onClose={onClose} />
    </Box>
  ) : null
}

type ModalProps = {
  ingredient: any
  isOpen: boolean
  onClose: () => void
}

const ProductIngredientModal: React.FC<ModalProps> = ({ ingredient, isOpen, onClose }) => {
  return (
    <Modal onClose={onClose} isOpen={isOpen} isCentered size="2xl">
      <ModalOverlay />
      <ModalContent marginX="24px">
        <ModalCloseButton marginTop="5px" />
        <Center gap="16px" px="24px" py={{ base: "38px", md: "60px" }}>
          <ResolvedImage image={ingredient?.image} borderRadius="full" boxSize="40%" />
          <Box>
            <Heading>{ingredient?.title}</Heading>
            <ResolvedContent content={ingredient?.description} />
          </Box>
        </Center>
      </ModalContent>
    </Modal>
  )
}

export default React.memo(ProductTabIngredients)
